import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Drawer,
    IconButton,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import { isEqual } from 'lodash';
import Image from 'mui-image';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { DataAPIContext, GET_ONE, UPDATE } from '../api';
import FixedLinearProgress from '../components/FixedLinearProgress';
import DeleteIconButton from '../components/DeleteIconButton';
import ImageActionButton from '../components/ImageActionButton';
import FormActionButtons from '../components/FormActionButtons';
import FormLicenseeContext, { toLicenseeId } from '../components/FormLicenseeContext';
import imagesAPI from '../components/images';
import LatLngPicker from '../components/LatLngPicker';
import LicenseeFormField from '../components/LicenseeFormField';
import PageMeta from '../components/PageMeta';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import { a11yProps } from '../components/TabPanel';
import useLeavePrompt from '../components/useLeavePrompt';
import UsedByPanel from '../components/UsedByPanel';
import { ContextStore } from '../store/contextStore';
import { toCoord } from './utils';

const MAX_STOPS = 3;
const PREVIEW_W = 418;
const PREVIEW_H = 272;

// What the leave prompt compares against the loaded attraction.
const formSnapshot = (f) => ({
    name: f.name,
    description: f.description,
    latitude: toCoord(f.latitude),
    longitude: toCoord(f.longitude),
    category: f.category?.id ?? null,
    tourOperatorId: toLicenseeId(f.tourOperatorId),
    imageChanged: f.imageFile !== undefined,
    stops: f.stops.map((s) => ({ id: s.id ?? null, distance: s.distance })),
});

const AttractionEdit = ({ onSaved }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { settings, isFranchise, hasPerm, currentLicensee } = React.useContext(ContextStore);
    const hasWritePerm = hasPerm('vehicle_fleet', 'write');
    const history = useHistory();
    const { id } = useParams();
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [currentTab, setCurrentTab] = React.useState(0);

    const [name, setName] = React.useState('');
    const [description, setDescription] = React.useState('');
    const [latitude, setLatitude] = React.useState('');
    const [longitude, setLongitude] = React.useState('');
    const [category, setCategory] = React.useState(null);
    // Image state. `imageSrc` is what we render:
    //   - string (blob: URL or absolute URL) → render directly
    //   - { p, s } object → resolve via imagesAPI.url
    //   - null → no image (show UPLOAD button)
    // `imageFile` is the pending change sentinel:
    //   - undefined → no change
    //   - File → new upload
    //   - null → clear
    const [imageSrc, setImageSrc] = React.useState(null);
    const [imageFile, setImageFile] = React.useState(undefined);
    // stops: [{ id, name, distance }] — distance is a string (user input).
    const [stops, setStops] = React.useState([]);
    const [tourOperatorId, setTourOperatorId] = React.useState(currentLicensee || null);
    const [loadedForm, setLoadedForm] = React.useState(null);

    const hasChanges = loadedForm !== null && !isEqual(
        formSnapshot({ name, description, latitude, longitude, category, tourOperatorId, imageFile, stops }),
        loadedForm
    );
    const leaveWithoutPrompt = useLeavePrompt(hasChanges && hasWritePerm);

    const onLicenseeChange = (id) => {
        setTourOperatorId(id);
    };

    // X, CANCEL and the backdrop all land here; the leave prompt asks first.
    const close = () => history.push('/bus-tours/attractions');

    React.useEffect(() => {
        setLoading(true);
        dataAPI(GET_ONE, 'attraction', { id })
            .then((res) => {
                const data = res.data || {};
                const loaded = {
                    name: data.name || '',
                    description: data.description || '',
                    latitude: data.latitude ?? '',
                    longitude: data.longitude ?? '',
                    category: data.category || null,
                    tourOperatorId: data.tour_operator ?? null,
                    imageFile: undefined,
                    stops: (data.stops || []).map((s) => ({
                        id: s.id ?? s.stop?.id,
                        name: s.name ?? s.stop?.name ?? '',
                        distance: s.distance != null ? String(s.distance) : '',
                    })),
                };
                setName(loaded.name);
                setDescription(loaded.description);
                setLatitude(loaded.latitude);
                setLongitude(loaded.longitude);
                setCategory(loaded.category);
                const firstImage = (data.images || [])[0];
                setImageSrc(firstImage?.image || null);
                setTourOperatorId(loaded.tourOperatorId);
                setStops(loaded.stops);
                setLoadedForm(formSnapshot(loaded));
            })
            .catch(() => enqueueSnackbar('Error loading attraction', { variant: 'error' }))
            .finally(() => setLoading(false));
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const onPickImage = (ev) => {
        const file = ev.target.files?.[0];
        if (file) {
            setImageFile(file);
            setImageSrc(URL.createObjectURL(file));
        }
    };

    const clearImage = () => {
        setImageFile(null);
        setImageSrc(null);
    };

    const addStopRow = () => {
        if (stops.length >= MAX_STOPS) return;
        setStops((prev) => [...prev, { id: null, name: '', distance: '' }]);
    };

    const updateStop = (index, patch) => {
        setStops((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
    };

    const removeStop = (index) => {
        setStops((prev) => prev.filter((_, i) => i !== index));
    };

    const onSave = () => {
        const body = {
            name,
            description: description || null,
            latitude: latitude !== '' && latitude !== null ? parseFloat(latitude) : null,
            longitude: longitude !== '' && longitude !== null ? parseFloat(longitude) : null,
            category,
            tour_operator: toLicenseeId(tourOperatorId),
            stops: stops
                .filter((s) => s.id != null)
                .map((s) => ({
                    id: s.id,
                    distance: s.distance !== '' ? parseFloat(s.distance) : null,
                })),
            ...(imageFile === null ? { images: [] } : {}),
        };
        const hasFile = imageFile instanceof File;
        const params = hasFile
            ? { id, data: body, files: { image: imageFile }, upload: true }
            : { id, data: body };

        // Success navigates away and unmounts the form, so only failures reset `saving`.
        setSaving(true);
        dataAPI(UPDATE, 'attraction', params)
            .then((res) => {
                if (res.statusCode && res.statusCode >= 400) {
                    enqueueSnackbar(res.data?.message || 'Error saving attraction', { variant: 'error' });
                    setSaving(false);
                } else {
                    enqueueSnackbar('Attraction saved', { variant: 'success' });
                    if (onSaved) onSaved();
                    leaveWithoutPrompt(close);
                }
            })
            .catch(() => {
                enqueueSnackbar('Error saving attraction', { variant: 'error' });
                setSaving(false);
            });
    };

    // Resolve the <img> src: blob:/absolute URL passes through, {p,s} resolves
    // via the images CDN preset matching the preview box.
    const renderedImageSrc = React.useMemo(() => {
        if (!imageSrc) return null;
        if (typeof imageSrc === 'string') return imageSrc;
        return imagesAPI.url(settings?.images?.base, imageSrc, `cover_${PREVIEW_W}x${PREVIEW_H}`);
    }, [imageSrc, settings]);

    return (
        <FormLicenseeContext.Provider value={toLicenseeId(tourOperatorId)}>
            <PageMeta title={name ? `Edit ${name}` : 'Edit attraction'} />
            <FixedLinearProgress visibility={saving} />
            <Drawer
                anchor="right"
                open
                onClose={close}
                PaperProps={{ sx: { width: { xs: '100%', sm: 400 }, overflowY: 'auto' } }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {/* Header */}
                    <Box
                        sx={{
                            bgcolor: '#f5f5f5',
                            px: 2,
                            py: 2.5,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            position: 'sticky',
                            top: 0,
                            zIndex: 2,
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>Edit attraction</Typography>
                        <IconButton onClick={close} aria-label="close">
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Tabs */}
                    <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} indicatorColor="primary" textColor="primary">
                        <Tab label="General" {...a11yProps(0)} />
                        {id && <Tab label="Used by" {...a11yProps(1)} />}
                    </Tabs>

                    {/* Body */}
                    {loading ? (
                        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <CircularProgress />
                        </Box>
                    ) : currentTab === 1 && id ? (
                        <Box sx={{ flex: 1, p: 2 }}>
                            <UsedByPanel resource="attraction" id={id} layout="sidebar" />
                        </Box>
                    ) : (
                        <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <LicenseeFormField value={tourOperatorId} onChange={onLicenseeChange} disabled={!hasWritePerm} />
                            <TextField
                                variant="filled"
                                label="Attraction Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                fullWidth
                                size="small"
                                required
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                variant="filled"
                                label="Description (Optional)"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                fullWidth
                                size="small"
                                multiline
                                minRows={3}
                                InputLabelProps={{ shrink: true }}
                            />
                            <LatLngPicker
                                latitude={latitude}
                                longitude={longitude}
                                mapHeight={175}
                                onUpdate={({ latitude: lat, longitude: lng }) => {
                                    setLatitude(lat ?? '');
                                    setLongitude(lng ?? '');
                                }}
                                textFieldProps={{ InputLabelProps: { shrink: true } }}
                            />

                            <RemoteAutocomplete
                                resource="activity_category"
                                urlSuffix={null}
                                label="Category"
                                variant="filled"
                                size="small"
                                fullWidth
                                value={category}
                                onChange={(ev, value) => setCategory(value || null)}
                                getOptionLabel={(opt) => opt?.title || ''}
                                isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                                optional
                            />

                            {renderedImageSrc ? (
                                <Box sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden' }}>
                                    <Image
                                        src={renderedImageSrc}
                                        cover
                                        height={PREVIEW_H}
                                        width="100%"
                                        style={{ borderRadius: 8, display: 'block' }}
                                    />
                                    <Box sx={{ position: 'absolute', top: 11, right: 11 }}>
                                        <DeleteIconButton title="Delete image" overImage allowed={hasWritePerm} onClick={clearImage} />
                                    </Box>
                                    <ImageActionButton
                                        hasImage
                                        overImage
                                        component="label"
                                        disabled={!hasWritePerm}
                                        sx={{ position: 'absolute', bottom: 12, right: 12 }}
                                    >
                                        <input type="file" hidden accept="image/*" onChange={onPickImage} />
                                    </ImageActionButton>
                                </Box>
                            ) : (
                                <ImageActionButton component="label" disabled={!hasWritePerm} sx={{ alignSelf: 'flex-start' }}>
                                    <input type="file" hidden accept="image/*" onChange={onPickImage} />
                                </ImageActionButton>
                            )}

                            <Divider />

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontSize: 18, fontWeight: 500 }}>
                                    Nearest bus stops (up to {MAX_STOPS})
                                </Typography>
                                {stops.map((s, idx) => (
                                    <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <RemoteAutocomplete
                                                resource="route_stop"
                                                urlSuffix={null}
                                                label="Bus Stop"
                                                variant="filled"
                                                size="small"
                                                value={s.id != null ? { id: s.id, name: s.name } : null}
                                                onChange={(ev, value) =>
                                                    updateStop(idx, {
                                                        id: value?.id ?? null,
                                                        name: value?.name || '',
                                                    })
                                                }
                                                getOptionLabel={(opt) => opt?.name || ''}
                                                isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                                                optional
                                            />
                                        </Box>
                                        <TextField
                                            variant="filled"
                                            label="Distance (m)"
                                            type="number"
                                            value={s.distance}
                                            onChange={(e) => updateStop(idx, { distance: e.target.value })}
                                            size="small"
                                            sx={{ width: 100 }}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                        <DeleteIconButton title="Remove stop" allowed={hasWritePerm} onClick={() => removeStop(idx)} />
                                    </Box>
                                ))}
                                {stops.length < MAX_STOPS && (
                                    <Button
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={addStopRow}
                                        sx={{ alignSelf: 'flex-start', mt: 1 }}
                                    >
                                        ADD BUS STOP
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    )}

                    {/* Footer */}
                    <Box
                        sx={{
                            borderTop: '1px solid #d8d8d8',
                            p: 2,
                            display: 'flex',
                            gap: 1,
                            position: 'sticky',
                            bottom: 0,
                            bgcolor: 'background.paper',
                            zIndex: 2,
                        }}
                    >
                        <FormActionButtons
                            mode="edit"
                            onSubmit={onSave}
                            submitDisabled={loading || saving || !name || !hasChanges} // CP-8441: waits for a change
                            onCancel={close}
                            size="medium"
                            sx={{ flex: 1 }}
                        />
                    </Box>
                </Box>
            </Drawer>
        </FormLicenseeContext.Provider>
    );
};

export default AttractionEdit;
